import { showSignUpModalAtom } from '@/atoms/chatAtoms';
import { getPopularSourceHomepagesForGeoCountry } from '@/components/chat/constants/unauthenticatedChatPopularSources';
import { getFaviconUrl } from '@/utils/faviconCache';
import { Button } from '@heidi/design-system/button';
import { Link } from '@heidi/design-system/link';
import {
  TypographyCaption,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { getCountryFromGeo } from '@heidi/growth/data';
import { useMediaQuery } from '@heidi/utils';
import { useTrackSignUpWallViewed } from '@/hooks/useTrackSignUpWallViewed';
import { cn } from '@/lib/cn';
import { type WidgetMessage } from '@/lib/embed.utils';
import { EvidenceSignUpWall } from '@/models/analytics/features/evidence';
import { getSupportedLocaleOrDefault } from '@/utils/i18n/supportedLocales';
import { prefixWithLocale } from '@heidi/growth/data';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

function SourceBadge({ homeUrl, label }: { homeUrl: string; label: string }) {
  const [hideIcon, setHideIcon] = useState(false);
  return (
    <div className="flex h-[22px] shrink-0 items-center gap-0.5 rounded-md py-1.5">
      {!hideIcon && (
        <Image
          src={getFaviconUrl(homeUrl)}
          alt=""
          width={16}
          height={16}
          unoptimized
          className="size-4 shrink-0 rounded-sm"
          onError={() => setHideIcon(true)}
        />
      )}
      <TypographyCaption className="leading-none text-text-secondary">{label}</TypographyCaption>
    </div>
  );
}

const BADGE_SCROLL_SEC = 32;

export function UnauthenticatedChatSignupCard({ className }: { className?: string }) {
  const intl = useIntl();
  const router = useRouter();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const setShowSignUpModal = useSetAtom(showSignUpModalAtom);
  const trackSignUpWallViewed = useTrackSignUpWallViewed();

  const { data: geoCountry } = useQuery({
    queryKey: ['geo-country'],
    queryFn: getCountryFromGeo,
    staleTime: Infinity,
  });

  const popularSourceHomepages = useMemo(
    () => getPopularSourceHomepagesForGeoCountry(geoCountry),
    [geoCountry]
  );

  const locale = getSupportedLocaleOrDefault(router.locale);

  const postToParent = useCallback((message: WidgetMessage) => {
    try {
      if (window.parent !== window) {
        window.parent.postMessage(message, '*');
      }
    } catch (e) {
      console.error('[Embed] Failed to post message to parent:', e);
    }
  }, []);

  const onSignUp = () => {
    trackSignUpWallViewed(EvidenceSignUpWall.HistoryWall);
    setShowSignUpModal(true);
    postToParent({ type: 'heidi:modalOpened' });
  };

  const loginHref = prefixWithLocale(locale, '/scribe/chat');
  const regionAriaLabel = intl.formatMessage({
    defaultMessage: 'Sign up for Heidi Evidence',
    id: 'aOgKSxwrGs',
  });

  const badgeRow = ({ duplicateA11y, keyPrefix }: { duplicateA11y: boolean; keyPrefix: string }) => (
    <>
      {popularSourceHomepages.map(({ homeUrl, label }) => (
        <SourceBadge key={`${keyPrefix}${homeUrl}`} homeUrl={homeUrl} label={label} />
      ))}
      {duplicateA11y
        ? popularSourceHomepages.map(({ homeUrl, label }) => (
            <div key={`${keyPrefix}${homeUrl}-dup`} aria-hidden>
              <SourceBadge homeUrl={homeUrl} label={label} />
            </div>
          ))
        : null}
    </>
  );

  return (
    <div
      role="region"
      aria-label={regionAriaLabel}
      data-testid="unauthenticated-chat-signup-card"
      className={cn('relative mx-1 mb-2 shrink-0', className)}
    >
      <div
        className={cn(
          'flex flex-col gap-4 rounded-xl border border-border bg-surface py-3',
          'shadow-sm'
        )}
      >
        <div className="flex flex-col gap-2">
          <div className="px-4 pt-0.5">
            <TypographyP2Bold className="text-text-primary">
              <FormattedMessage
                defaultMessage="Sign up for advanced features" id="BCWc7BWnLH"
              />
            </TypographyP2Bold>
          </div>

          <div className="relative min-w-0 px-4">
            {prefersReducedMotion ? (
              <div className="flex w-max items-center gap-3 overflow-x-auto pb-0.5 scrollbar-none">
                {badgeRow({ duplicateA11y: false, keyPrefix: 'static-' })}
              </div>
            ) : (
              <div
                className={cn(
                  'group relative overflow-x-hidden',
                  'pb-0.5'
                )}
              >
                <div
                  className={cn(
                    'unauth-badge-scroll-marquee flex w-max items-center gap-3 will-change-transform',
                    'group-hover:[animation-play-state:paused]'
                  )}
                  style={
                    {
                      '--unauth-badge-scroll-sec': `${BADGE_SCROLL_SEC}s`,
                    } as CSSProperties
                  }
                >
                  {badgeRow({ duplicateA11y: true, keyPrefix: 'marq-' })}
                </div>
              </div>
            )}
            <div
              className="pointer-events-none absolute inset-y-0 left-4 z-10 w-2 bg-gradient-to-r from-surface via-surface/35 to-transparent"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-4 z-10 w-2 bg-gradient-to-l from-surface via-surface/35 to-transparent"
              aria-hidden
            />
          </div>

          <div className="px-4 py-1">
            <TypographyCaption className="leading-snug text-text-primary">
              <FormattedMessage
                defaultMessage="Control your sources, access chat history, and use local guidelines to inform your answers." id="YvVxfuVLBo"
              />
            </TypographyCaption>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 pb-1 pt-0">
          <Button variant="ghost" size="xs" asChild>
            <Link href={loginHref}>
              <FormattedMessage
                defaultMessage="Log in" id="/9k/6XT2zU"
              />
            </Link>
          </Button>
          <Button
            variant="default"
            size="xs"
            onClick={onSignUp}
            data-testid="unauthenticated-chat-signup-button"
          >
            <FormattedMessage
              defaultMessage="Sign up free" id="NRhfnFf9Uv"
            />
          </Button>
        </div>
      </div>
    </div>
  );
}
